/**
 * Company: SPHMedia
 * Description: [A customizable carousel component for displaying a list of items]
 */

import { Children, ReactElement, ReactNode, useEffect } from "react";
import { twMerge } from "tailwind-merge";

import CustomCarouselButton from "./CustomCarouselButton";
import { useCustomCarousel } from "./hooks/useCustomCarousel";

type CustomCarouselProps = {
  children: ReactNode;
  title?: ReactNode;
  slidesPerView?: number;
  gap?: number;
  step?: number;

  showNavigation?: boolean;
  draggable?: boolean;

  onSlideChange?: (index: number) => void;

  className?: string;
  headerClassName?: string;
  navigationClassName?: string;
  containerClassName?: string;
  viewportClassName?: string;
  trackClassName?: string;
};

/**
 * CustomCarousel
 * Description: [A customizable carousel component for displaying a list of items]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function CustomCarousel({
  children,
  title,
  slidesPerView = 3.2,
  gap = 20,
  step = 1,
  className,
  headerClassName,
  navigationClassName,
  containerClassName,
  viewportClassName,
  trackClassName,
  showNavigation = true,
  draggable = false,
  onSlideChange,
}: CustomCarouselProps): ReactElement {
  const slides = Children.toArray(children);

  const {
    viewportRef,
    slideWidth,
    translateX,
    next,
    prev,
    canGoNext,
    canGoPrev,
    currentIndex,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave,
  } = useCustomCarousel({
    totalSlides: slides.length,
    slidesPerView,
    gap,
    step,
    draggable,
  });

  useEffect(() => {
    onSlideChange?.(currentIndex);
  }, [currentIndex, onSlideChange]);

  return (
    <div
      data-testid="carousel-wrapper"
      className={twMerge(
        "w-full lg:max-w-screen-lg xl:max-w-screen-bh-cxl",
        className,
      )}
    >
      {title && (
        <div
          className={twMerge(
            "mb-2.5 flex items-center justify-between",
            headerClassName,
          )}
        >
          <div>{title}</div>
        </div>
      )}

      <div
        data-testid="carousel-container"
        className={twMerge("flex gap-2.5 items-center", containerClassName)}
      >
        {showNavigation && (
          <div className={twMerge(navigationClassName)}>
            <CustomCarouselButton
              direction="prev"
              variant={canGoPrev ? "enabled" : "disabled"}
              onClick={prev}
            />
          </div>
        )}
        <div
          data-testid="carousel-viewport"
          ref={viewportRef}
          className={twMerge("overflow-hidden", viewportClassName)}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerLeave}
          style={{
            touchAction: draggable ? "pan-y" : undefined,
          }}
        >
          <div
            data-testid="carousel-track"
            className={twMerge(
              "flex transition-transform duration-300 ease-out",
              trackClassName,
            )}
            style={{
              gap: `${gap}px`,
              transform: `translateX(-${translateX}px)`,
            }}
          >
            {slides.map((child, index) => (
              <div
                data-testid="carousel-slide"
                key={index}
                className="shrink-0"
                style={{
                  width: slideWidth,
                }}
              >
                {child}
              </div>
            ))}
          </div>
        </div>
        {showNavigation && (
          <div className={twMerge(navigationClassName)}>
            <CustomCarouselButton
              direction="next"
              variant={canGoNext ? "enabled" : "disabled"}
              onClick={next}
            />
          </div>
        )}
      </div>
    </div>
  );
}
